import { getInvestorRepository } from "@/infrastructure/di";
import type {
  CreateIndividualInvestorRequest,
  CreateCompanyInvestorRequest,
  HubInvitationRequest,
  InvestorStatus,
  InvestorLegalRepCreateRequest,
  InvestorLegalRepUpdateRequest,
  UpdateIndividualInvestorRequest,
  UpdateCompanyInvestorRequest,
} from "@/domain/investor";
import { InvalidationEntity } from "../entityDependencies";
import { useCreateMutation } from "../createMutation";
import { investorCache, type InvestorPatch } from "./cache";

export const useCreateIndividualInvestor = (orgId: string) =>
  useCreateMutation({
    mutationFn: (data: CreateIndividualInvestorRequest) =>
      getInvestorRepository().createIndividual(data),
    cache: (c) => c.create(investorCache.at(orgId)),
    errorLogMessage: "Failed to create individual investor:",
  });

export const useCreateCompanyInvestor = (orgId: string) =>
  useCreateMutation({
    mutationFn: (data: CreateCompanyInvestorRequest) => getInvestorRepository().createCompany(data),
    cache: (c) => c.create(investorCache.at(orgId)),
    errorLogMessage: "Failed to create company investor:",
  });

export const useUpdateIndividualInvestor = (orgId: string) =>
  useCreateMutation({
    mutationFn: ({ uuid, data }: { uuid: string; data: UpdateIndividualInvestorRequest }) =>
      getInvestorRepository().updateIndividual(uuid, data),
    cache: (c) =>
      c.patch(investorCache.at(orgId), {
        id: ({ uuid }) => uuid,
        fromResponse: (investor) => ({ investor }),
      }),
    errorLogMessage: "Failed to update individual investor:",
  });

export const useUpdateCompanyInvestor = (orgId: string) =>
  useCreateMutation({
    mutationFn: ({ uuid, data }: { uuid: string; data: UpdateCompanyInvestorRequest }) =>
      getInvestorRepository().updateCompany(uuid, data),
    cache: (c) =>
      c.patch(investorCache.at(orgId), {
        id: ({ uuid }) => uuid,
        fromResponse: (investor) => ({ investor }),
      }),
    errorLogMessage: "Failed to update company investor:",
  });

export const useCreateInvestorLegalRepresentative = (orgId: string) =>
  useCreateMutation({
    mutationFn: ({ uuid, data }: { uuid: string; data: InvestorLegalRepCreateRequest }) =>
      getInvestorRepository().createLegalRepresentative(uuid, data),
    cache: (c) =>
      c.invalidate({ orgId, entities: [InvalidationEntity.INVESTOR], facets: ["details"] }),
    errorLogMessage: "Failed to create investor legal representative:",
  });

export const useUpdateInvestorLegalRepresentative = (orgId: string) =>
  useCreateMutation({
    mutationFn: ({
      uuid,
      representativeUuid,
      data,
    }: {
      uuid: string;
      representativeUuid: string;
      data: InvestorLegalRepUpdateRequest;
    }) => getInvestorRepository().updateLegalRepresentative(uuid, representativeUuid, data),
    cache: (c) =>
      c.invalidate({ orgId, entities: [InvalidationEntity.INVESTOR], facets: ["details"] }),
    errorLogMessage: "Failed to update investor legal representative:",
  });

export const useDeleteInvestorLegalRepresentative = (orgId: string) =>
  useCreateMutation({
    mutationFn: ({ uuid, representativeUuid }: { uuid: string; representativeUuid: string }) =>
      getInvestorRepository().deleteLegalRepresentative(uuid, representativeUuid),
    cache: (c) =>
      c.invalidate({ orgId, entities: [InvalidationEntity.INVESTOR], facets: ["details"] }),
    errorLogMessage: "Failed to delete investor legal representative:",
  });

export const useDeleteInvestor = (orgId: string) =>
  useCreateMutation({
    mutationFn: (uuid: string) => getInvestorRepository().delete(uuid),
    cache: (c) => c.remove(investorCache.at(orgId), { id: (uuid) => uuid }),
    errorLogMessage: "Failed to delete investor:",
  });

export const useSetInvestorStatus = (orgId: string) =>
  useCreateMutation({
    mutationFn: ({ uuid, status }: { uuid: string; status: InvestorStatus }) => {
      const repository = getInvestorRepository();
      if (status === "archived") return repository.archive(uuid);
      return status === "active" ? repository.activate(uuid) : repository.deactivate(uuid);
    },
    cache: (c) =>
      c.patch(investorCache.at(orgId), {
        id: ({ uuid }) => uuid,
        optimistic: ({ status }) => ({ status }),
      }),
    errorLogMessage: "Failed to change investor status:",
  });

export const useBulkSetInvestorStatus = (orgId: string) =>
  useCreateMutation({
    mutationFn: ({ uuids, status }: { uuids: readonly string[]; status: InvestorStatus }) => {
      const repository = getInvestorRepository();
      if (status === "archived") return repository.bulkArchive(uuids);
      return status === "active"
        ? repository.bulkActivate(uuids)
        : repository.bulkDeactivate(uuids);
    },
    cache: (c) =>
      c.bulk(investorCache.at(orgId), {
        action: "patch",
        ids: ({ uuids }) => uuids,
        optimistic: ({ status }) => ({ status }),
        result: (result) => result,
      }),
    errorLogMessage: "Failed to change investor status in bulk:",
  });

export const useBulkDeleteInvestors = (orgId: string) =>
  useCreateMutation({
    mutationFn: (uuids: readonly string[]) => getInvestorRepository().bulkDelete(uuids),
    cache: (c) =>
      c.bulk(investorCache.at(orgId), {
        action: "remove",
        ids: (uuids) => uuids,
        result: (result) => result,
      }),
    errorLogMessage: "Failed to delete investors in bulk:",
  });

export const useInviteInvestorToHub = (orgId: string) =>
  useCreateMutation({
    mutationFn: ({ uuid, request }: { uuid: string; request?: HubInvitationRequest }) =>
      getInvestorRepository().inviteToHub(uuid, request),
    cache: (c) =>
      c.patch(investorCache.at(orgId), {
        id: ({ uuid }) => uuid,
        optimistic: (): InvestorPatch => ({ hub_status: "pending" }),
      }),
    errorLogMessage: "Failed to invite investor to hub:",
  });

export const useResendInvestorHubInvitation = (orgId: string) =>
  useCreateMutation({
    mutationFn: ({
      invitationUuid,
      request,
    }: {
      invitationUuid: string;
      request?: HubInvitationRequest;
    }) => getInvestorRepository().resendHubInvitation(invitationUuid, request),
    cache: (c) =>
      c.invalidate({ orgId, entities: [InvalidationEntity.INVESTOR], facets: ["details"] }),
    errorLogMessage: "Failed to resend investor hub invitation:",
  });

export const useRevokeInvestorHubInvitation = (orgId: string) =>
  useCreateMutation({
    mutationFn: (invitationUuid: string) =>
      getInvestorRepository().revokeHubInvitation(invitationUuid),
    cache: (c) =>
      c.invalidate({ orgId, entities: [InvalidationEntity.INVESTOR], facets: ["details"] }),
    errorLogMessage: "Failed to revoke investor hub invitation:",
  });

export const useRevokeInvestorHubAccess = (orgId: string) =>
  useCreateMutation({
    mutationFn: (uuid: string) => getInvestorRepository().revokeHubAccess(uuid),
    cache: (c) =>
      c.patch(investorCache.at(orgId), {
        id: (uuid) => uuid,
        optimistic: (): InvestorPatch => ({ hub_status: "none" }),
      }),
    errorLogMessage: "Failed to revoke investor hub access:",
  });
